import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { DNDContext } from '../../contexts/DNDContext';
import { useDNDColumns } from '../../hooks/useDNDColumns';
import { DraggableColumnItem } from './functionalComponents/DraggableColumnItem';
import Button from '../Button';
import usePost from '../../hooks/usePost';
import useFetch from '../../hooks/useFetch';
import config from '../../config';
import TextInput from '../Inputs/TextInput';
import SelectInput from '../Inputs/SelectInput';
import TextareaInput from '../Inputs/TextareaInput';
import { CurrentTeamContext } from '../../contexts/CurrentTeamContext';
import { PlusIcon } from '../SVGIcons/SVGIcons';
import orderStatusOptions from '../Options/orderStatusOptions';
import Loading from '../Loading/Loading';
import ListingPagination from '../Pagination/ListingPagination';
import { mapOrdersToDNDColumns, parseProcessingStatus } from '../../helpers';
import EmptyState from '../EmptyState/EmptyState';

export default function SidebarDragAndDrop({ contentReference, callback }) {
	const { DNDState, DNDDispatch } = useContext(DNDContext);
	const { teamState } = useContext(CurrentTeamContext);

	const { post } = usePost();
	const { fetch } = useFetch();

	const [statusOptions, setStatusOptions] = useState([
		{ name: 'All', value: 'all' },
		{ name: 'Pending', value: 1 },
		{ name: 'Accepted', value: 2 },
		{ name: 'Processing', value: 3 },
		{ name: 'Completed', value: 4 },
		{ name: 'Dispatched', value: 5 },
	]);
	const [formData, setFormData] = useState({});
	const [loading, setLoading] = useState(false);
	const [pages, setPages] = useState(0);
	const [filter, setFilter] = useState({
		status: 'all',
		limit: 10,
		skip: 0,
	});

	const region = 'sidebarOrderItems';

	const allColumns = DNDState?.columns || [];

	const scopedColumns = useMemo(() => {
		return allColumns.filter((col) => col.region === region);
	}, [allColumns, region]);

	const topOfPage = useRef(null);

	const [formOpen, setFormOpen] = useState(null);

	async function createOrderItem(column) {
		const { id, header } = column;
		let res = await post(
			{ ...formData, orderUuid: id, teamUuid: teamState?.currentTeam?.teamUuid, orderRef: header },
			`${config.apiv1}/order/item.create`
		);

		if (res.data) {
			let convertedField = {
				...res.data,
				id: res.data.itemUuid,
				orderRef: res.data.orderRef,
				itemDesc: res.data.itemDesc,
				note: res.data.note,
				packageUuid: res.data.packageUuid,
			};
			let newColumn = { ...column, fields: [...column.fields, convertedField] };

			setFormData({});
			setFormOpen(null);
			DNDDispatch({
				type: 'MERGE_COLUMNS',
				columns: [newColumn],
			});
		}
	}

	function getTeamName(column, teamUuid) {
		if (column.sellerTeamUuid === teamUuid) {
			return column.buyerTeamName;
		} else {
			return column.sellerTeamName;
		}
	}

	function handlePagination(data) {
		let selected = data.selected;
		let newSkip = Math.ceil(selected * filter.limit);
		handleFilter(newSkip, 'skip');
		topOfPage?.current?.scrollIntoView({ behavior: 'smooth' });
	}

	function handleData(value, name) {
		setFormData((formData) => ({
			...formData,
			[name]: value,
		}));
	}

	function handleFilter(value, name) {
		let skip = null;
		if (name === 'status') {
			skip = 0;
			setPages();
		}

		setFilter((filter) => ({
			...filter,
			skip: skip !== null ? skip : filter?.skip,
			[name]: value?.value || value,
		}));
	}

	async function buildURLAndFetch(filter) {
		setLoading(true);
		const url = new URL(`${config.apiv1}/order/orders.read/${teamState?.currentTeam?.teamUuid}`);

		url.searchParams.set('includeOrderItems', 'true');
		url.searchParams.set('includeOrderItems', 'true');
		url.searchParams.set('outputType', 'full');

		filter?.status && filter?.status !== 'all' && url.searchParams.set('status', filter?.status);
		filter?.search && url.searchParams.set('search', filter?.search);
		filter?.limit && url.searchParams.set('limit', filter?.limit);
		filter?.skip && url.searchParams.set('skip', filter?.skip);

		let res = await fetch(url.href, null, true);

		if (res?.ok) {
			const formattedColumns = mapOrdersToDNDColumns(res?.data, contentReference?.pack?.packageUuid, region);

			DNDDispatch({
				type: 'REPLACE_COLUMNS_BY_REGION',
				region,
				columns: formattedColumns,
			});
			setPages(res?.data && res.data.length > 0 ? Math.ceil(res?.totalDocuments / filter?.limit) : 0);
		} else {
			setPages(0);
		}

		setLoading(false);
	}

	useEffect(() => {
		buildURLAndFetch(filter);
	}, [filter]);

	useEffect(() => {
		DNDDispatch({
			type: 'SET_DND_CALLBACKS',
			onDragStartExtended: contentReference.onDragStartExtended,
			onDragEndExtended: (data) => callback(data),
		});
	}, [contentReference]);

	return (
		<div className='sidebarColumns'>
			<div ref={topOfPage}></div>
			<h4 className='sidebarHeader'>Order items</h4>
			<div className='sidebarFilterBar'>
				<div style={{ width: '40%' }}>
					<h4 className='label'>Order status</h4>
					<SelectInput
						onlyInput
						options={statusOptions}
						callback={handleFilter}
						name='status'
						value={filter?.status}
						defaultValue={filter?.status}
					/>
				</div>
				<div style={{ width: '60%' }}>
					<h4 className='label'>Search</h4>
					<TextInput onlyInput name='search' value={filter?.search} callback={handleFilter} controlled />
				</div>
			</div>
			{loading ? (
				<Loading type='circle' />
			) : (
				<>
					{scopedColumns?.length !== 0 ? (
						scopedColumns?.map((column) => (
							<Droppable
								key={column.id}
								droppableId={column.id}
								direction='vertical'
								isDropDisabled={contentReference.immutable === true}>
								{(provided) => (
									<div className='dropSection' ref={provided.innerRef} {...provided.droppableProps}>
										<div className='header'>
											<h4>{column.header}</h4>
											{
												parseProcessingStatus(column?.orderProcessingStatus, 'biggerIcon')
													.iconWhite
											}
										</div>
										<div className='sectionBody'>
											{column.subHeader && <h4 className='hintText'>{column.subHeader}</h4>}
											<div className='columnItems'>
												<h4
													style={{
														display: 'flex',
														paddingBottom: '6px',
														justifyContent: 'space-between',
														fontSize: '0.813em',
													}}>
													Unassigned accompaniments
													{formOpen !== column.id && (
														<Button
															text='Add'
															iconLeft={<PlusIcon />}
															style='secondary'
															size='xs'
															onClick={() => setFormOpen(column.id)}
														/>
													)}
												</h4>
												{column.fields?.length !== 0 ? (
													column.fields?.map((field, index) => (
														<DraggableColumnItem
															key={field.id}
															field={field}
															index={index}
															onClick={contentReference?.onDragItemClick}
														/>
													))
												) : (
													<p style={{ color: 'gray', margin: '0px' }}>No accompaniments</p>
												)}
											</div>
											{provided.placeholder}
											{formOpen === column.id ? (
												<div>
													<TextInput
														text={'Item description'}
														name={'itemDesc'}
														callback={handleData}
														defaultValue={formData?.itemDesc}
													/>
													<TextareaInput
														text={'Note'}
														name={'note'}
														callback={handleData}
														defaultValue={formData?.note}
													/>
													<div
														style={{
															display: 'flex',
															justifyContent: 'center',
															marginTop: '8px',
															gap: '12px',
														}}>
														<Button
															text='Submit'
															style='primary'
															onClick={() => createOrderItem(column)}
														/>
														<Button
															text='Cancel'
															style='secondary'
															onClick={() => {
																setFormOpen(null);
																setFormData({});
															}}
														/>
													</div>
												</div>
											) : (
												<></>
											)}
										</div>
										<div className='footer'>
											<h4>{getTeamName(column, teamState?.currentTeam?.teamUuid)}</h4>
										</div>
									</div>
								)}
							</Droppable>
						))
					) : (
						<EmptyState body='No orders matched your search filters' header='No orders found' />
					)}
				</>
			)}
			{pages > 0 && scopedColumns?.length !== 0 && (
				<ListingPagination
					handleClick={handlePagination}
					pages={pages}
					currentPage={filter?.skip / filter?.limit}
				/>
			)}
		</div>
	);
}
